import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosError, AxiosResponse } from "axios"
import { API_URL_FELL } from "config"
import { SiteDesignGetPayload, SiteDesignSettingsData, SiteDesignType, ServerSettingsData } from "lib/types"

export type SiteDesignsGetPayload = {
    data?: SiteDesignType[],
    projectId: number,
    siteId: number,
    error?: string
}

export const getSiteDesigns = createAsyncThunk<SiteDesignsGetPayload, { projectId: number, siteId: number }, { rejectValue: SiteDesignsGetPayload }>("siteDesign/getSiteDesigns",
    async (args, { rejectWithValue }) => {
        try {
            const apiResponse = await axios.get<any>(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign`)

            return {
                data: apiResponse.data.siteDesigns,
                projectId: args.projectId,
                siteId: args.siteId
            }
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId
            })
        }
    })

export type SiteDesignInsertPayload = {
    data?: SiteDesignType,
    projectId: number,
    siteId: number,
    error?: string
}

export const postSiteDesign = createAsyncThunk<SiteDesignInsertPayload, { projectId: number, siteId: number, dispatch }, { rejectValue: SiteDesignInsertPayload }>("siteDesign/postSiteDesigns",
    async (args, { rejectWithValue }) => {
        try {
            const siteDesignResponse = await axios.post<SiteDesignType>(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign`)

            return {
                data: siteDesignResponse.data,
                projectId: args.projectId,
                siteId: args.siteId
            }
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId
            })
        }
    })

export type SiteDesignUpdatePayload = {
    data?: SiteDesignType,
    projectId: number,
    siteId: number,
    error?: string
}

export const putSiteDesign = createAsyncThunk<SiteDesignUpdatePayload, { projectId: number, siteId: number, siteDesign: SiteDesignType, dispatch }, { rejectValue: SiteDesignUpdatePayload }>("siteDesign/putSiteDesigns",
    async (args, { rejectWithValue }) => {
        try {
            const apiResponse = await axios.put<SiteDesignType>(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesign.id}`, args.siteDesign)
            return {
                data: apiResponse.data,
                projectId: args.projectId,
                siteId: args.siteId
            }
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId
            })
        }
    })

export type SiteDesignDeletePayload = {
    designId: number,
    projectId: number,
    siteId: number,
    error?: string
}

export const deleteSiteDesign = createAsyncThunk<SiteDesignDeletePayload, { projectId: number, siteId: number, designId: number }, { rejectValue: SiteDesignDeletePayload }>("siteDesign/deleteSiteDesigns",
    async (args, { rejectWithValue }) => {
        try {
            const apiResponse = await axios.delete<SiteDesignType>(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.designId}`)
            return {
                data: apiResponse.data,
                projectId: args.projectId,
                siteId: args.siteId,
                designId: args.designId
            }
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId,
                designId: args.designId
            })
        }
    })

export const duplicateSiteDesign = createAsyncThunk<SiteDesignInsertPayload, { projectId: number, siteId: number, siteDesignId: number }, { rejectValue: string }>("siteDesign/duplicate",
    async ({ projectId, siteId, siteDesignId }, { rejectWithValue }) => {
        try {
            const siteDesignResponse = await axios.post<SiteDesignType>(`${API_URL_FELL()}/project/${projectId}/site/${siteId}/sitedesign/${siteDesignId}`)

            return {
                data: siteDesignResponse.data,
                projectId, siteId: siteId
            }
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue(error.message)
        }
    })

type FetchSiteDesignArgs = {
    projectId: number;
    siteId: number;
    siteDesignId: number;
}

export const fetchSiteDesign = createAsyncThunk<
    SiteDesignGetPayload,
    FetchSiteDesignArgs,
    { rejectValue: SiteDesignGetPayload }
>(
    'siteDesignEditor/fetchSiteDesign',
    async (args, { rejectWithValue, dispatch }) => {
        try {
            const response = await axios.get<SiteDesignGetPayload>(
                `${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesignId}`
            );

            const siteDesign = response.data.siteDesign;

            if (!siteDesign?.serverSettings?.huskyServerSettings) {
                const defaultsResult = await dispatch(getServerDefaultsThunk());

                if (getServerDefaultsThunk.fulfilled.match(defaultsResult)) {
                    const huskyDefaults = defaultsResult.payload?.data?.huskyServerSettings;

                    if (huskyDefaults) {
                        siteDesign.serverSettings.huskyServerSettings = huskyDefaults;
                    }
                } else {
                    console.warn("Failed to fetch server defaults, continuing without them.");
                }
            }

            return {
                ...response.data,
                siteDesign
            };
        } catch (err: any) {
            if (err.response) {
                return rejectWithValue(err.response.data as SiteDesignGetPayload);
            }
            throw err;
        }
    }
);


export type SiteDesignSaveChangesPayload = {
    projectId: number,
    siteId: number,
    siteDesignId: number;
    error?: string
}

export const saveChangesSiteDesign = createAsyncThunk<SiteDesignSaveChangesPayload, { projectId: number, siteId: number, siteDesignId: number, siteDesignData: SiteDesignSettingsData }, { rejectValue: SiteDesignSaveChangesPayload }>("siteDesign/saveChanges",
    async (args, { rejectWithValue }) => {
        try {
            const apiResponse = await axios.put<SiteDesignSettingsData>(`${API_URL_FELL()}/project/${args.projectId}/site/${args.siteId}/sitedesign/${args.siteDesignId}/savechanges`, args.siteDesignData)
            return {
                data: apiResponse.data,
                projectId: args.projectId,
                siteId: args.siteId,
                siteDesignId: args.siteDesignId
            }
        } catch (err) {
            const error: AxiosError<string> = err;
            return rejectWithValue({
                error: error.message,
                projectId: args.projectId,
                siteId: args.siteId,
                siteDesignId: args.siteDesignId
            })
        }
    })


export type ServerSettingsDefaultsGetPayload = {
    data?: ServerSettingsData;
    error: string;
};


export const getServerDefaultsThunk = createAsyncThunk<
    AxiosResponse<ServerSettingsData>,
    void,
    { rejectValue: string }
>('defaults/getServerDefaults', async (_, { rejectWithValue }) => {
    try {
        const response = await axios.get(`${API_URL_FELL()}/defaults`);
        return response;
    } catch (err) {
        const error: AxiosError<string> = err;
        return rejectWithValue(error.response?.data || 'Unknown error');
    }
});

